import {RETURN_PATH_SESSION_STORAGE_KEY} from '../constants'
import {isUnauthenticatedRoute} from './authRoutes'

// A crafted value must not be usable to stuff storage, and no legitimate route
// in this app comes close to this length.
const MAX_RETURN_PATH_LENGTH = 512

// Browsers strip tabs, newlines and carriage returns out of URLs entirely, so a
// value containing them can normalize into something structurally different
// *after* a naive check has already passed it.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/

// Bounded so a value encoded N deep can't spin here.
//
// Do NOT raise this to "be safer". A browser decodes the path exactly once, so
// pass 0 already sees everything it will ever act on; the extra passes only
// catch values crafted to look benign after one decode. Exhausting the budget
// fails safe: expandEncodings returns the partly-decoded string, which is then
// validated like any other, and a value still encoded that deep resolves to a
// same-origin path matching no route. Verified by the AB#25795 security review.
const MAX_DECODE_PASSES = 3

/**
 * Expand percent-encoding until the value stops changing, so a validator can see
 * what a browser would ultimately act on. `undefined` signals a malformed escape
 * sequence, which is itself disqualifying — nothing this app produces contains
 * one.
 */
const expandEncodings = (value: string): string | undefined => {
  let current = value

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    let next: string
    try {
      next = decodeURIComponent(current)
    } catch {
      return undefined
    }

    if (next === current) {
      return current
    }
    current = next
  }

  return current
}

/**
 * The structural rules a return path must satisfy. Written as a sequence of
 * early returns rather than a compound boolean so it stays readable and clear of
 * `typescript:S3776` (cognitive complexity).
 */
const isSafePath = (value: string): boolean => {
  if (value.length === 0 || value.length > MAX_RETURN_PATH_LENGTH) return false

  // Must be an origin-relative path. This alone rejects `https://evil.example/x`
  // and any other absolute URL.
  if (!value.startsWith('/')) return false

  // Protocol-relative: `//evil.example` is same-scheme, different-origin.
  if (value.startsWith('//')) return false

  // Browsers treat a backslash as a path separator in URLs, so `/\evil.example`
  // and `/\/evil.example` are protocol-relative in disguise. No legitimate path
  // in this app contains one.
  if (value.includes('\\')) return false

  if (CONTROL_CHARACTERS.test(value)) return false

  // A `..` segment resolves in the browser, so `/albums/../login` would land on
  // an unauthenticated route and loop the login flow past the guard below.
  if (value.split(/[/?#]/).includes('..')) return false

  // Sending the user back into the login flow they just completed is a redirect
  // loop, not a deep link.
  if (isUnauthenticatedRoute(value)) return false

  return true
}

/**
 * Validate a candidate post-login destination.
 *
 * The value reaching this function is under partial attacker control — it round
 * trips through `sessionStorage`, which any script on the origin (and the user
 * directly) can write — and its output is handed to `history.replace`. A bypass
 * here is an open redirect, so the checks run against BOTH the raw string and
 * its fully-decoded expansion: the raw form is what `history.replace` receives,
 * the decoded form is what a browser ultimately resolves. Validating only one of
 * the two is the standard way this class of guard is defeated.
 *
 * Returns the exact string that was validated, never a re-derived one. Decoding
 * is used for inspection only — returning the decoded value would corrupt query
 * strings that legitimately carry encoded delimiters (`%26` becoming a real `&`).
 *
 * @returns the safe path, or `undefined` for anything that fails a check.
 */
export const resolveReturnPath = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined
  if (!isSafePath(raw)) return undefined

  const expanded = expandEncodings(raw)
  if (expanded === undefined) return undefined
  if (!isSafePath(expanded)) return undefined

  return raw
}

/**
 * Remember where the user was headed before the Auth0 redirect took the page
 * away. A value that fails validation is dropped silently rather than stored —
 * a stale or crafted stash must never become a redirect.
 *
 * `sessionStorage` (not `localStorage`) because this is meaningful only across a
 * single login round trip in a single tab, and because the Auth0 hop is a full
 * document navigation that in-memory router state cannot survive.
 */
export const stashReturnPath = (raw: unknown): void => {
  const resolved = resolveReturnPath(raw)
  if (!resolved) return

  try {
    sessionStorage.setItem(RETURN_PATH_SESSION_STORAGE_KEY, resolved)
  } catch {
    // Safari private mode and blocked site data make storage throw. Losing the
    // deep link is a far better outcome than breaking login, so degrade quietly.
  }
}

/**
 * Read and consume the stashed path. Consumed exactly once: the entry is removed
 * whether or not it validates, so a poisoned value cannot linger and surprise a
 * later login.
 *
 * Re-validates on the way out. Storage is attacker-writable, so a write-side
 * check alone would leave the read path unguarded.
 */
export const takeReturnPath = (): string | undefined => {
  try {
    const stored = sessionStorage.getItem(RETURN_PATH_SESSION_STORAGE_KEY)
    sessionStorage.removeItem(RETURN_PATH_SESSION_STORAGE_KEY)
    return resolveReturnPath(stored)
  } catch {
    return undefined
  }
}

/**
 * Drop any stashed path. Called wherever a session ends so a value captured in
 * one session cannot redirect the next one.
 */
export const clearReturnPath = (): void => {
  try {
    sessionStorage.removeItem(RETURN_PATH_SESSION_STORAGE_KEY)
  } catch {
    // See stashReturnPath.
  }
}
